import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import type { Bank } from '@/hooks/useBanks';

type BanksTabProps = {
  banks: Bank[];
  managerEmail: string;
  onAddBank: (name: string, short: string, manager: string) => Promise<boolean>;
  onDeleteBank: (bankId: string, manager: string) => void;
};

export default function BanksTab({ banks, managerEmail, onAddBank, onDeleteBank }: BanksTabProps) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [short, setShort] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const openForm = () => {
    setName('');
    setShort('');
    setError(null);
    setShowForm(true);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim()) {
      setError(t('admin.banks.form.errorRequired'));
      return;
    }
    setSubmitting(true);
    const ok = await onAddBank(name, short, managerEmail);
    setSubmitting(false);
    if (!ok) {
      setError(t('common.error'));
      return;
    }
    setShowForm(false);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-xl font-semibold text-foreground-950">
            {t('admin.banks.title')}
          </h1>
          <p className="mt-1 text-sm text-foreground-600">{t('admin.banks.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={openForm}
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
        >
          <i className="ri-add-line"></i>
          {t('admin.banks.add')}
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100 text-xs uppercase tracking-wide text-foreground-500">
                <th className="px-5 py-3 font-semibold">{t('admin.banks.short')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.banks.name')}</th>
                <th className="px-5 py-3 font-semibold">{t('admin.clients.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {banks.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-5 py-10 text-center text-foreground-500">
                    {t('admin.banks.empty')}
                  </td>
                </tr>
              ) : (
                banks.map((bank) => (
                  <tr key={bank.id} className="border-b border-background-100 last:border-0 hover:bg-background-50">
                    <td className="px-5 py-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-xs font-semibold text-secondary-900">
                        {bank.short || bank.name.slice(0, 3).toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-semibold text-foreground-900">{bank.name}</td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => onDeleteBank(bank.id, managerEmail)}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 px-2.5 py-1.5 text-xs font-semibold text-accent-900 transition-colors hover:bg-accent-50"
                      >
                        <i className="ri-delete-bin-line"></i>
                        {t('admin.banks.delete')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <Modal title={t('admin.banks.form.title')} subtitle={t('admin.banks.form.subtitle')} onClose={() => setShowForm(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                {t('admin.banks.form.nameLabel')}
              </label>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t('admin.banks.form.namePh')}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                {t('admin.banks.form.shortLabel')}
              </label>
              <input
                type="text"
                value={short}
                onChange={(event) => setShort(event.target.value.toUpperCase())}
                placeholder={t('admin.banks.form.shortPh')}
                maxLength={5}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-accent-900">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                {t('admin.banks.form.cancel')}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
              >
                {submitting && <i className="ri-loader-4-line animate-spin"></i>}
                {t('admin.banks.form.submit')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}